/**
 * A popover (see /styleguide): what floats over the page from what opened it
 * (a button, an indicator), drawn as iOS's are, on the menus' surface. Below
 * what opened it, or above where there's more room; no taller than the
 * window's room, and kept off its edges. A press outside it or Escape closes
 * it. Menus (ui/Menu) are drawn in one; others hold a little content (the
 * streak's day, Public Texts' filters, a language picker).
 */
import * as preact from "preact";

import * as modalController from "modalController";
import { cn } from "shared/utils";

interface Props {
  open: boolean;
  /** A press outside it, or Escape, asking it to close */
  onClose: () => void;
  /** What opens it (its button), which it's anchored to */
  anchor: preact.ComponentChild;
  children?: preact.ComponentChildren;
  /** Which of its anchor's edges it lines up with */
  align?: "start" | "end";
  /** Pointing at its anchor, as it's opened unasked */
  arrow?: boolean;
  role?: preact.JSX.AriaRole;
  "aria-label"?: string;
  /** Its root's, holding its anchor */
  class?: string;
  /** The popover's own */
  popoverClass?: string;
}

interface State {
  placement?: Placement;
}

/** Where it fits the window, measured as it opens */
interface Placement {
  /** Too little room below its anchor, so shown above it */
  above: boolean;
  /** As tall as the room it has (in CSS pixels, scrolling the rest) */
  maxHeight?: number;
  /** Moved sideways, to stay clear of the window's edges */
  shift: number;
  /** Its arrow's distance from its left edge, under its anchor's centre */
  arrowLeft: number;
}

/** The gap kept between a popover and the window's edges */
const windowMargin = 8;
/** Below a popover, its shadow (menuSurface) */
const shadowRoom = 40;

export default class Popover extends preact.Component<Props, State> {
  state: State = {};
  private rootRef = preact.createRef<HTMLDivElement>();
  private popoverRef = preact.createRef<HTMLDivElement>();

  componentDidMount() {
    // As a press starts, not on its click: iOS sends no clicks from taps on
    // what isn't clickable (plain text, the page around), and another
    // popover's button keeps its click to itself
    document.addEventListener("pointerdown", this.closeOnPressOutside);
    document.addEventListener("keydown", this.closeOnEscape);
    window.addEventListener("resize", this.placeAgain);
    if (this.props.open) {
      this.placePopover();
    }
  }

  /** Whether it's closing with focus inside it */
  getSnapshotBeforeUpdate(previousProps: Props) {
    return (
      previousProps.open &&
      !this.props.open &&
      Boolean(this.popoverRef.current?.contains(document.activeElement))
    );
  }

  componentDidUpdate(
    previousProps: Props,
    _previousState: State,
    focusWasInside: boolean
  ) {
    if (this.props.open && !previousProps.open) {
      this.placePopover();
    } else if (!this.props.open && previousProps.open) {
      this.closed(focusWasInside);
    }
  }

  componentWillUnmount() {
    document.removeEventListener("pointerdown", this.closeOnPressOutside);
    document.removeEventListener("keydown", this.closeOnEscape);
    window.removeEventListener("resize", this.placeAgain);
    if (this.props.open) {
      this.closed(false);
    }
  }

  /**
   * Gives focus back to its anchor, from what it removed, and the Web
   * Reader's toolbar frame the room it took (unless a dialog has it now)
   */
  private closed(focusWasInside: boolean) {
    if (focusWasInside) {
      (this.rootRef.current?.firstElementChild as HTMLElement | null)?.focus();
    }
    this.setState({ placement: undefined });
    if (!modalController.isShown()) {
      modalController.requestModalSpace(0);
    }
  }

  private closeOnPressOutside = (event: PointerEvent) => {
    if (
      this.props.open &&
      !this.rootRef.current?.contains(event.target as Node)
    ) {
      this.props.onClose();
    }
  };

  private closeOnEscape = (event: KeyboardEvent) => {
    if (this.props.open && event.key === "Escape") {
      this.props.onClose();
    }
  };

  /** Measured afresh as the window changes (the Web Reader's frame growing) */
  private placeAgain = () => {
    if (this.props.open) {
      this.setState({ placement: undefined }, () => this.placePopover());
    }
  };

  /**
   * Below its anchor, or above it where there's more room; no taller than
   * that room, and moved sideways off the window's edges
   */
  private placePopover() {
    const popover = this.popoverRef.current;
    const root = this.rootRef.current;
    if (!popover || !root) {
      return;
    }
    const rect = popover.getBoundingClientRect();
    const rootRect = root.getBoundingClientRect();
    // The Web Reader's toolbar frame grows to hold it (bookmarklet.ts), and
    // its shadow
    modalController.requestModalSpace(rect.bottom + shadowRoom);
    // Window pixels per CSS pixel, as a page's zoom (phones') scales it
    const scale = rect.height / popover.offsetHeight || 1;
    const gap = rect.top - rootRect.bottom;
    const spaceBelow = window.innerHeight - windowMargin - rect.top;
    const spaceAbove = rootRect.top - gap - windowMargin;
    const above = rect.height > spaceBelow && spaceAbove > spaceBelow;
    const space = above ? spaceAbove : spaceBelow;
    const windowWidth = document.documentElement.clientWidth;
    const shift =
      Math.max(windowMargin - rect.left, 0) +
      Math.min(windowWidth - windowMargin - rect.right, 0);
    const anchorRect = (root.firstElementChild ?? root).getBoundingClientRect();
    this.setState({
      placement: {
        above,
        maxHeight: rect.height > space ? space / scale : undefined,
        shift: shift / scale,
        arrowLeft:
          (anchorRect.left + anchorRect.width / 2 - rect.left - shift) / scale,
      },
    });
  }

  render() {
    const { open, align, arrow, popoverClass } = this.props;
    const { placement } = this.state;
    return (
      <div
        class={cn({
          popoverAnchor: true,
          open,
          [this.props.class ?? ""]: Boolean(this.props.class),
        })}
        ref={this.rootRef}
      >
        {this.props.anchor}
        {open ? (
          <div
            class={cn({
              popover: true,
              [popoverClass ?? ""]: Boolean(popoverClass),
              alignStart: align === "start",
              alignEnd: align !== "start",
              withArrow: arrow,
              above: placement?.above,
              scrolls: placement?.maxHeight !== undefined,
            })}
            style={
              placement && {
                maxHeight: placement.maxHeight,
                transform: placement.shift
                  ? `translateX(${placement.shift}px)`
                  : undefined,
              }
            }
            role={this.props.role}
            aria-label={this.props["aria-label"]}
            ref={this.popoverRef}
          >
            {arrow && placement ? (
              <span
                class="popoverArrow"
                style={{ left: placement.arrowLeft }}
              ></span>
            ) : null}
            {this.props.children}
          </div>
        ) : null}
      </div>
    );
  }
}
